import Head from 'next/head';
import Link from 'next/link';

export default function Terms() {
  const lastUpdated = "March 17, 2026";

  return (
    <>
      <Head>
        <title>Terms of Service | L.I.S.T Learning Pathways</title>
        <meta name="description" content="Terms of Service for L.I.S.T Learning Pathways - Understanding our platform's terms and conditions." />
      </Head>

      <main className="min-h-screen bg-white">
        {/* Document Hero */}
        <section className="bg-gray-50 border-b border-gray-100 pt-32 pb-20 relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-gray-900">Terms of Service</h1>
            <div className="flex items-center justify-center gap-4 text-gray-500 text-base">
              <span className="w-12 h-px bg-brand-primary/20"></span>
              <span>Last Updated: {lastUpdated}</span>
              <span className="w-12 h-px bg-brand-primary/20"></span>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100">
              <div className="prose prose-lg max-w-none prose-headings:text-brand-dark prose-headings:font-extrabold prose-p:text-gray-600 prose-li:text-gray-600 prose-headings:tracking-tight">
                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 text-brand-primary">1. Agreement to Terms</h2>
                <p>
                  By accessing or using L.I.S.T Learning Pathways, you agree to be bound by these Terms of Service
                  and all applicable laws and regulations. If you do not agree with any of these terms, you
                  are prohibited from using or accessing this strategic learning platform.
                </p>

                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 mt-12 text-brand-primary">2. Use License</h2>
                <p>
                  Upon purchasing a course or subscribing to our platform, we grant you a limited, non-exclusive,
                  non-transferable license to access and use our premium content for personal, non-commercial purposes.
                  This is the grant of a license, not a transfer of title.
                </p>

                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 mt-12 text-brand-primary">3. Payment Terms</h2>
                <p>
                  All payments are processed securely through our verified global payment providers. By making a purchase, you agree to:
                </p>
                <div className="grid gap-4">
                  {[
                    "Provide current, complete, and accurate purchase information",
                    "Promptly update your account and payment information",
                    "Pay all charges at the prices then in effect for your purchases"
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 group hover:border-brand-primary/30 transition-all">
                      <div className="w-8 h-8 rounded-full bg-brand-dark text-white flex items-center justify-center font-bold text-sm shrink-0">{i + 1}</div>
                      <p className="m-0 font-medium text-gray-700">{item}</p>
                    </div>
                  ))}
                </div>
                <section className="mb-12">
                  <h2 className="text-2xl mt-4 font-bold text-brand-dark mb-6">Training Terms & Conditions</h2>
                  <div className="prose prose-brand max-w-none text-gray-600 space-y-4">
                    <p>By enrolling in any program offered by L.I.S.T Learning Pathways, you agree to the following terms:</p>
                    <ol className="list-[lower-alpha] pl-6 space-y-4">
                      <li>
                        <strong>Intellectual Property Rights:</strong> All course materials, content, logos, software, and related documentation provided during the training are the exclusive intellectual property of the provider and are protected under applicable copyright and intellectual property laws.
                      </li>
                      <li>
                        <strong>Usage Restrictions:</strong> Participants are strictly prohibited from copying, reproducing, distributing, renting, sublicensing, modifying, or reverse-engineering any course materials, software, or source code. Any violation may result in legal action and immediate termination of access to the course.
                      </li>
                      <li>
                        <strong>Bookings and Payments:</strong> Course registrations are subject to availability and L.I.S.T confirmation. All applicable course fees must be paid in accordance with the agreed payment terms. The training schedule approved by L.I.S.T shall be considered final. Any modifications to the schedule must be mutually agreed upon by the buyer and L.I.S.T.
                      </li>
                      <li>
                        <strong>Learning Outcomes:</strong> L.I.S.T provides training services intended for educational purposes only and does not guarantee the acquisition of specific knowledge, certification outcomes, or job suitability.
                      </li>
                      <li>
                        <strong>Access to E-Learning Courses:</strong> Access to e-learning courses may commence from the official course completion date or activation date determined by L.I.S.T, and not necessarily from the participant&apos;s first login.
                      </li>
                      <li>
                        <strong>Code of Conduct:</strong> L.I.S.T reserves the right to refuse admission, suspend participation, or terminate course access without refund if a participant engages in inappropriate, disruptive, or unethical behaviour during the training program.
                      </li>
                      <li>
                        <strong>Disclaimer:</strong> All training materials and content are provided solely for educational purposes. They should not be interpreted as professional, legal, financial, or technical advice for third parties.
                      </li>
                      <li>
                        <strong>Dispute Resolution and Jurisdiction:</strong> Any disputes arising out of or in connection with these terms or the training services shall be subject to the exclusive jurisdiction of the courts located in Mumbai, India.
                      </li>
                    </ol>
                  </div>
                </section>
                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 mt-12 text-brand-primary">4. User Responsibilities</h2>
                <p>
                  As an executive learner on our platform, you are responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-3">
                  <li>Maintaining the strict confidentiality of your account credentials</li>
                  <li>All activities that occur under your institutional or individual account</li>
                  <li>Ensuring your use of the platform complies with all international digital laws</li>
                </ul>

                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 mt-12 text-brand-primary">5. Content Usage</h2>
                <p>
                  All content provided on L.I.S.T Learning Pathways is protected under global copyright laws. You may not:
                </p>
                <div className="flex flex-wrap gap-2 mb-8">
                  {["No Distribution", "No Commercial Use", "No Modifications", "No Derivatives"].map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-red-50 text-red-600 text-xs font-bold rounded-full uppercase tracking-wider border border-red-100">{tag}</span>
                  ))}
                </div>

                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 mt-12 text-brand-primary">6. Termination</h2>
                <p>
                  We reserve the right to terminate or suspend access to our service immediately,
                  without prior notice or liability, for any breach of these Terms.
                </p>

                <h2 className="text-2xl font-extrabold border-b border-gray-100 pb-4 mb-6 mt-12 text-brand-primary">7. Contact Information</h2>
                <div className="bg-brand-dark text-white p-8 rounded-2xl">
                  <p className="mb-6 font-medium text-gray-300">Questions about the Terms of Service should be sent to our legal department:</p>
                  <div className="flex flex-col sm:flex-row gap-8">
                    <div>
                      <span className="block text-brand-primary font-bold uppercase text-[10px] tracking-[0.2em] mb-2">Primary Email</span>
                      <a href="mailto:connect@list.academy" className="text-lg font-bold hover:text-brand-primary transition-colors">connect@list.academy</a>
                    </div>
                    <div>
                      <span className="block text-brand-primary font-bold uppercase text-[10px] tracking-[0.2em] mb-2">Corporate Office</span>
                      <address className="not-italic text-sm text-gray-300 font-medium">
                        Mumbai, India • Andheri West
                      </address>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
